import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  OnDestroy,
  Signal,
  computed,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ObfuscationService,
  ObfuscationType,
} from '@principle-theorem/ng-management-tools';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import {
  TypedFormControl,
  TypedFormGroup,
  formControlChanges$,
  isDisabled$,
  validFormControlChanges$,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  CustomChartType,
  ENABLED_CHART_TYPES,
  ICustomReportChart,
  MeasureFormatter,
  MeasureReducer,
  ReportFilterOption,
} from '@principle-theorem/principle-core/interfaces';
import { ReportBuilderDataSourceInstance } from '@principle-theorem/reporting';
import {
  ICanBeChartedProperty,
  ICanGroupMeasuresProperty,
  IGroupBySection,
} from '@principle-theorem/reporting/interfaces';
import { IGroup, uid } from '@principle-theorem/shared';
import { type ComparableValue, type Crossfilter } from 'crossfilter2';
import {
  compact,
  first,
  groupBy as groupByLodash,
  isString,
  sortBy,
} from 'lodash';
import { ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import {
  IChartDisplay,
  getChartDisplay,
} from '../../report-builder-chart-filters/report-builder-chart-section/report-builder-chart/report-builder-chart-display';
import { ReportBuilderManageChartBloc } from './report-builder-manage-chart-bloc';

export interface IReportBuilderAddChartRequest<T extends object = object> {
  measures: ICanBeChartedProperty[];
  groupBys: IGroupBySection[];
  filteredData: T[];
  dataSource: ReportBuilderDataSourceInstance;
  ndx?: Crossfilter<T>;
  chart?: ICustomReportChart;
}

export interface IReportBuilderAddChartResponse {
  label: string;
  type: CustomChartType;
  measure: ICanBeChartedProperty;
  groupBy: ICanGroupMeasuresProperty;
  reducer: MeasureReducer;
  filters: ComparableValue[];
}

@Component({
  selector: 'pr-report-builder-add-chart-dialog',
  templateUrl: './report-builder-add-chart-dialog.component.html',
  styleUrls: ['./report-builder-add-chart-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
  standalone: false,
})
export class ReportBuilderAddChartDialogComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _obfuscation = inject(ObfuscationService);
  form = new TypedFormGroup<IReportBuilderAddChartResponse>({
    type: new TypedFormControl(CustomChartType.Row, Validators.required),
    label: new TypedFormControl('New Chart', Validators.required),
    measure: new TypedFormControl(undefined, Validators.required),
    filters: new TypedFormControl([]),
    reducer: new TypedFormControl(MeasureReducer.Sum),
    groupBy: new TypedFormControl(undefined, Validators.required),
  });
  filterSearchCtrl = new TypedFormControl<string>('');
  filteredOptions: Signal<IGroup<ReportFilterOption, string>[]>;
  chartTypes: CustomChartType[] = ENABLED_CHART_TYPES;
  isDisabled$ = isDisabled$(this.form);
  display$ = new ReplaySubject<IChartDisplay | undefined>(1);
  bloc: ReportBuilderManageChartBloc;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: IReportBuilderAddChartRequest,
    public _dialogRef: MatDialogRef<
      ReportBuilderAddChartDialogComponent,
      IReportBuilderAddChartResponse
    >,
    private _scope: CurrentScopeFacade
  ) {
    this.bloc = new ReportBuilderManageChartBloc(
      this._scope.currentBrand$,
      of(data.dataSource),
      this.data.filteredData,
      this._onDestroy$,
      this.form.controls,
      [MeasureFormatter.Link],
      (column) => column.measure.metadata.availableAsChart !== false
    );

    combineLatest([
      formControlChanges$(this.form.controls.measure),
      formControlChanges$(this.form.controls.reducer),
    ])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([measure, reducer]) => {
        const measureLabel = measure?.metadata.label;
        const labelAlreadySet = !!data.chart?.label;
        if (!measureLabel || labelAlreadySet) {
          return;
        }

        let label = '';

        switch (reducer) {
          case MeasureReducer.Count:
            label = measureLabel;
            break;
          case MeasureReducer.Average:
            label = `Average ${measureLabel}`;
            break;
          case MeasureReducer.Sum:
            if (measureLabel.includes('Total')) {
              label = `${measureLabel}`;
              break;
            }
            label = `Total ${measureLabel}`;
            break;
          default:
            return;
        }

        this.form.controls.label.setValue(label, { emitEvent: false });
        this.form.controls.label.markAsDirty();
        this.form.controls.label.markAsTouched();
        this.form.controls.label.updateValueAndValidity();
      });

    validFormGroupChanges$(this.form)
      .pipe(
        debounceTime(500),
        map((form) => {
          const chart: ICustomReportChart = {
            uid: uid(),
            type: form.type,
            label: form.label,
            groupBy: form.groupBy.metadata.id,
            settings: [
              {
                uid: uid(),
                label: form.measure.metadata.label,
                dataPoint: form.measure.metadata.id,
                reducer: form.reducer,
                filters: form.filters,
              },
            ],
            builderConfig: {
              rotateXAxisLabels:
                form.type === CustomChartType.Column ? true : false,
            },
            builderData: {
              colourOverride: !!form.groupBy.groupMeasure.colourAccessor,
              plottedOverTime: [
                MeasureFormatter.Timestamp,
                MeasureFormatter.Day,
              ].includes(
                form.groupBy.metadata.formatter ?? MeasureFormatter.Text
              )
                ? true
                : false,
            },
          };
          return getChartDisplay(
            data.dataSource,
            chart,
            data.ndx,
            this._buildLabelTransformFn()
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((display) => this.display$.next(display));

    if (!data.chart) {
      this.form.controls.groupBy.setValue(
        first(data.groupBys.flatMap((section) => section.options))?.measure
      );
    }

    if (data.chart) {
      const chart = data.chart;
      const setting = first(chart.settings);
      const measure = setting
        ? data.measures.find(
            (availableMeasure) =>
              availableMeasure.metadata.id === setting.dataPoint
          )
        : undefined;

      if (!measure) {
        // eslint-disable-next-line no-console
        console.error(
          `No measure found for ${setting?.dataPoint}`,
          data.measures
        );
      }

      const groupBy = data.groupBys
        .flatMap((section) => section.options)
        .find(
          (availableGroupBy) =>
            availableGroupBy.measure.metadata.id === chart.groupBy
        )?.measure;

      this.form.patchValue({
        type: chart.type,
        label: chart.label,
        measure,
        groupBy,
        reducer: setting?.reducer,
        filters: setting?.filters ?? [],
      });

      this.bloc.dataPointSearchCtrl.setValue(measure?.metadata.label ?? '');
      this.bloc.groupBySearchCtrl.setValue(groupBy?.metadata.label ?? '');
      this.form.markAsDirty();
      this.form.markAsTouched();
      this.form.updateValueAndValidity();
    }

    const search = toSignal(validFormControlChanges$(this.filterSearchCtrl), {
      initialValue: '',
    });

    const options = toSignal(this.bloc.filterOptions$, {
      initialValue: [],
    });

    this.filteredOptions = computed(() =>
      this._getFilteredOptions(options(), search())
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }
    this._dialogRef.close(this.form.getRawValue());
  }

  compareFn(valueA?: ComparableValue, valueB?: ComparableValue): boolean {
    if (valueA === undefined || valueB === undefined) {
      return false;
    }

    return valueA === valueB;
  }

  private _getFilteredOptions(
    allOptions: ReportFilterOption[],
    search?: string
  ): IGroup<ReportFilterOption, string>[] {
    const groupedOptions = Object.entries(
      groupByLodash(allOptions, (option) => (option.isDeleted ? 'Deleted' : ''))
    ).map(([group, items]) => ({
      group,
      items: sortBy(items, (option) => option.label.toLowerCase()),
    }));

    const filteredOptions = this._filterOptions(groupedOptions, search);
    return sortBy(filteredOptions, (option) => option.group.toLowerCase());
  }

  private _filterOptions(
    options: IGroup<ReportFilterOption, string>[],
    search?: string | ReportFilterOption
  ): IGroup<ReportFilterOption, string>[] {
    if (!search || !isString(search)) {
      return options;
    }
    const searchValue = search?.trim().toLowerCase();

    return compact(
      options.map((option) => {
        const items = option.items.filter((item) =>
          item.label.toLowerCase().includes(searchValue)
        );

        if (!items.length) {
          return;
        }

        return {
          ...option,
          items,
        };
      })
    );
  }

  private _buildLabelTransformFn(): (
    label: string,
    piiType?: string
  ) => string {
    return (label: string, piiType?: string): string =>
      this._obfuscation.transform(
        label,
        (piiType as ObfuscationType) ?? 'name'
      );
  }
}
